import { cn } from '../../shared/lib/cn';
import { calendarDay, sizeText } from '../../shared/lib/format';
import { GROUP_LABEL } from '../../shared/ui/group-label';
import { isUsableSample, testSampleLine, type TBookmark } from '../../entities/bookmark';
import { SampleMenu } from './sample-menu';

const COLUMNS = {
  name: 'min-w-0 flex-1',
  source: 'w-col-event-type shrink-0',
  size: 'w-col-origin shrink-0 text-right',
  since: 'w-col-origin shrink-0',
  sample: 'w-col-verdict shrink-0',
  menu: 'w-control-sm shrink-0',
} as const;

export const BookmarkHeader = () => (
  <div role="row" className={cn('flex h-control-xs items-center gap-4 px-inset', GROUP_LABEL)}>
    <span role="columnheader" className={COLUMNS.name}>
      Name
    </span>
    <span role="columnheader" className={COLUMNS.source}>
      Came from
    </span>
    <span role="columnheader" className={COLUMNS.size}>
      Size
    </span>
    <span role="columnheader" className={COLUMNS.since}>
      Kept since
    </span>
    <span role="columnheader" className={COLUMNS.sample}>
      Test sample in
    </span>
    <span aria-hidden className={COLUMNS.menu} />
  </div>
);

interface IBookmarkRowProps {
  bookmark: TBookmark;
  editable: boolean;
  now: number;
  timeZone?: string;
  onUseIn: (flowId: string) => void;
  onRemove: () => void;
}

export const BookmarkRow = ({
  bookmark,
  editable,
  now,
  timeZone,
  onUseIn,
  onRemove,
}: IBookmarkRowProps) => {
  const usable = isUsableSample(bookmark);

  return (
    <div
      role="row"
      className="flex min-h-row-xl items-center gap-4 border-t border-border-row px-inset py-label"
    >
      <span
        role="cell"
        className={cn(
          COLUMNS.name,
          'font-mono text-data break-all',
          usable ? 'text-primary' : 'text-faint',
        )}
      >
        {bookmark.name}
      </span>
      <span role="cell" className={cn(COLUMNS.source, 'text-data break-words text-muted')}>
        {bookmark.sourceName ?? (
          <span title={bookmark.notUsableReason ?? undefined}>Source deleted</span>
        )}
      </span>
      <span role="cell" className={cn(COLUMNS.size, 'font-mono text-data text-primary')}>
        {bookmark.sizeBytes === null ? '—' : sizeText(bookmark.sizeBytes)}
      </span>
      <span role="cell" className={cn(COLUMNS.since, 'text-data text-muted')}>
        {calendarDay(bookmark.createdAt, now, timeZone)}
      </span>
      <span role="cell" className={cn(COLUMNS.sample, 'text-data break-words text-muted')}>
        {testSampleLine(bookmark)}
      </span>
      <span role="cell" className={cn(COLUMNS.menu, 'flex justify-end')}>
        <SampleMenu bookmark={bookmark} editable={editable} onUseIn={onUseIn} onRemove={onRemove} />
      </span>
    </div>
  );
};
